import { useMemo } from 'react';
import type { SelectProps } from '@mantine/core';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect/RepositorySelect';
import { useUserRepository } from '../rpc';

/**
 * Пропсы UserSelect — стандартный `SelectProps` от Mantine без полей, которые
 * компонент задаёт сам (data, loading), плюс value/onChange в терминах user.id.
 */
export interface UserSelectProps
  extends Omit<SelectProps, 'data' | 'loading' | 'value' | 'onChange'> {
  /** ID выбранного пользователя (или null если ничего не выбрано). */
  value: number | null | undefined;
  /** Вызывается при изменении: id выбранного пользователя или null при очистке. */
  onChange: (userId: number | null) => void;
  /**
   * Предзагруженная опция — гарантированно присутствует в data с первого рендера.
   * Используется, чтобы лейбл предвыбранного пользователя показывался до загрузки
   * полного списка (например, текущий пользователь в форме создания).
   */
  initialOption?: { value: string; label: string };
  /**
   * Включить всех пользователей, включая неактивных и системных.
   * По умолчанию false — показываются только активные не-системные (onlyHumanActive).
   */
  includeAll?: boolean;
  /**
   * Ограничить список пользователями с указанной ролью (или любой из списка)
   * по `role_code` — через `userRepository.filterByRole`. Удобно для доменных
   * пикеров (напр. сюрвейеры с ролью MWS_SURVEYOR).
   */
  roleCode?: string | string[];
}

/**
 * UserSelect — выпадающий список пользователей для полей ответственного/менеджера.
 *
 * Инкапсулирует:
 *  - `userRepository.onlyHumanActive()` — фильтр активных не-системных пользователей
 *  - `orderBy('name', 'asc')` — алфавитная сортировка
 *  - серверный поиск по имени/email (через `search()` в RepositorySelect)
 *
 * @example
 * <UserSelect
 *   label="Ответственный"
 *   placeholder="Ответственный..."
 *   value={SchemaForm.values.deal_owner_id}
 *   onChange={(id) => SchemaForm.setFieldValue('deal_owner_id', id)}
 *   initialOption={initialSelectOptions?.deal_owner_id}
 * />
 */
export function UserSelect({
  value,
  onChange,
  includeAll = false,
  roleCode,
  ...selectProps
}: UserSelectProps) {
  const userRepository = useUserRepository();

  const repo = useMemo(() => {
    const base = includeAll ? userRepository : userRepository.onlyHumanActive();
    const scoped = roleCode != null ? base.filterByRole(roleCode) : base;
    // orderByName использует COLLATE "ru-x-icu" — корректная алфавитная сортировка
    // кириллицы при БД в en_US.UTF-8
    return scoped.orderByName('asc');
  }, [userRepository, includeAll, roleCode]);

  return (
    <RepositorySelect
      repo={repo}
      displayField='name'
      {...selectProps}
      value={value == null ? '' : String(value)}
      onChange={v => onChange(v ? parseInt(v, 10) : null)}
    />
  );
}

export default UserSelect;
